import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit2, Trash2, RotateCcw, Users } from "lucide-react";

function UserModal({ user: editUser, companies, isSuperAdmin, currentCompanyId, onSave, onClose }) {
  const [form, setForm] = useState({
    email: editUser?.email || "", name: editUser?.name || "",
    password: "", role: editUser?.role || "company_user",
    company_id: editUser?.company_id || currentCompanyId || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    if (!form.email || !form.name) { setError("Email and name required"); return; }
    if (!editUser && !form.password) { setError("Password required for new users"); return; }
    setSaving(true);
    try { await onSave(form); onClose(); }
    catch (e) { setError(formatApiError(e)); setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{editUser ? "Edit" : "Create"} User</DialogTitle></DialogHeader>
        {error && <div className="p-2 bg-red-50 rounded text-red-700 text-sm">{error}</div>}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-gray-500">Full Name *</Label><Input className="mt-1" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div><Label className="text-xs text-gray-500">Email *</Label><Input className="mt-1" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} disabled={!!editUser} /></div>
          </div>
          {!editUser && <div><Label className="text-xs text-gray-500">Temporary Password * (user will be forced to change)</Label><Input className="mt-1" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} /></div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Role</Label>
              <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                  <SelectItem value="company_admin">Company Admin</SelectItem>
                  <SelectItem value="company_user">Company User</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isSuperAdmin && (
              <div>
                <Label className="text-xs text-gray-500">Company</Label>
                <Select value={form.company_id || "__none__"} onValueChange={v => setForm(f => ({ ...f, company_id: v === "__none__" ? "" : v }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="None (super admin)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {companies.map(c => <SelectItem key={c._id} value={c._id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save User"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UserManagementPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const isSuperAdmin = currentUser?.role === "super_admin";

  useEffect(() => {
    load();
    if (isSuperAdmin) client.get("/companies/").then(r => setCompanies(r.data)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    const res = await client.get("/companies/users").catch(() => ({ data: [] }));
    setUsers(res.data);
    setLoading(false);
  };

  const save = async (form) => {
    if (editingUser) await client.put(`/companies/users/${editingUser._id}`, form);
    else await client.post("/companies/users", form);
    await load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    await client.delete(`/companies/users/${id}`);
    load();
  };

  const toggle = async (u) => {
    await client.put(`/companies/users/${u._id}`, { is_active: !u.is_active });
    load();
  };

  const doReset = async () => {
    if (!resetPw || resetPw.length < 6) return;
    await client.post(`/companies/users/${resetUserId}/reset-password`, { password: resetPw });
    setResetUserId(null); setResetPw("");
    alert("Password reset. User will be prompted on next login.");
  };

  const roleBadge = { super_admin: "bg-purple-100 text-purple-700", company_admin: "bg-[#e6f2f5] text-[#006080]", company_user: "bg-gray-100 text-gray-600" };
  const companyName = (id) => companies.find(c => c._id === id)?.name || id || "–";

  return (
    <div className="p-6 max-w-5xl mx-auto fade-in">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold text-gray-900">User Management</h1><p className="text-gray-500 text-sm">Create and manage users within your organization</p></div>
        <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={() => { setEditingUser(null); setShowModal(true); }}><Plus size={16} className="mr-2" /> Add User</Button>
      </div>
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-white border border-gray-200 rounded-lg animate-pulse" />)}</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full data-table text-sm">
            <thead><tr><th className="px-4 py-3 text-left">Name</th><th className="px-4 py-3 text-left">Email</th>{isSuperAdmin && <th className="px-4 py-3 text-left">Company</th>}<th className="px-4 py-3 text-left">Role</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">Actions</th></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u._id}>
                  <td className="px-4 py-2.5 font-medium">{u.name}</td>
                  <td className="px-4 py-2.5 text-gray-500 font-mono text-xs">{u.email}</td>
                  {isSuperAdmin && <td className="px-4 py-2.5 text-xs text-gray-500">{companyName(u.company_id)}</td>}
                  <td className="px-4 py-2.5"><span className={`text-xs px-2 py-0.5 rounded font-medium ${roleBadge[u.role] || "bg-gray-100"}`}>{u.role?.replace(/_/g, " ")}</span></td>
                  <td className="px-4 py-2.5"><Badge variant="secondary" className={`text-xs ${u.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>{u.is_active ? "Active" : "Disabled"}</Badge></td>
                  <td className="px-4 py-2.5">
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => { setEditingUser(u); setShowModal(true); }}><Edit2 size={11} /></Button>
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => setResetUserId(u._id)} title="Reset password"><RotateCcw size={11} /></Button>
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => toggle(u)}>{u.is_active ? "Disable" : "Enable"}</Button>
                      {u._id !== currentUser?._id && u.role !== "super_admin" && (
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs text-red-500 hover:bg-red-50" onClick={() => del(u._id)}><Trash2 size={11} /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Password reset modal */}
      {resetUserId && (
        <Dialog open onOpenChange={() => setResetUserId(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Reset Password</DialogTitle></DialogHeader>
            <p className="text-xs text-gray-500">Set a new temporary password. The user will be forced to change it on next login.</p>
            <Input type="password" value={resetPw} onChange={e => setResetPw(e.target.value)} placeholder="New temporary password (min. 6 chars)" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetUserId(null)}>Cancel</Button>
              <Button className="bg-[#006080] text-white" onClick={doReset} disabled={resetPw.length < 6}>Reset Password</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {showModal && <UserModal user={editingUser} companies={companies} isSuperAdmin={isSuperAdmin} currentCompanyId={currentUser?.company_id} onSave={save} onClose={() => { setShowModal(false); setEditingUser(null); }} />}
    </div>
  );
}
